import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import FormInputSearch from 'shared/components/form-input-search'
import { Loader } from 'shared/components/loader'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import { useSearchLectures } from '../api/get-search-lectures'
import { SEARCH_LECTURES_QUERY_PARAM } from '../consts/search-lectures'
import { useGetCoursePageUrl } from '../hooks/use-course-path-url'
import { CourseId } from '../types/course-interface'

export const CourseSearchForm = ({
  courseId,
  className,
}: {
  courseId: CourseId
  className?: string
}) => {
  const { t } = useTranslation()
  const router = useRouter()

  const [isOpen, setIsOpen] = useState(false)

  const { [SEARCH_LECTURES_QUERY_PARAM]: initialSearchParam } = router.query as Partial<
    Record<typeof SEARCH_LECTURES_QUERY_PARAM, string | undefined>
  >
  const coursePath = useGetCoursePageUrl()
  const form = useForm({
    defaultValues: {
      search: initialSearchParam || '',
    },
  })
  const search = form.watch('search')
  const [searchValue, _] = useDebouncedValue(search, 500)
  const getSearchLecturesQuery = useSearchLectures({ courseId, search: searchValue })

  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen])
  const containerRef = useClickOutside(handleClickOutside)

  const handleClear = () => {
    form.setValue('search', '')
    if (typeof router.query?.[SEARCH_LECTURES_QUERY_PARAM] !== 'undefined') {
      const { [SEARCH_LECTURES_QUERY_PARAM]: _removedSearch, ...rest } = router.query
      router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
    }
    setIsOpen(false)
  }

  if (!coursePath) return null

  return (
    <div ref={containerRef} className={twMerge('relative w-full min-w-[150px]', className)}>
      <FormInputSearch
        className="w-full"
        value={search}
        onChange={e => {
          setIsOpen(true)
          form.setValue('search', e.target.value)
        }}
        onFocus={() => setIsOpen(true)}
        onClear={handleClear}
        placeholder={t('global.search')}
      />

      {isOpen && searchValue && (
        <div className="absolute inset-x-0 z-20 mt-1 w-full overflow-hidden rounded-lg border border-text-secondary/30 bg-background-secondary text-text-primary shadow-lg">
          {getSearchLecturesQuery.isLoading || getSearchLecturesQuery.data === undefined ? (
            <div className="flex justify-center px-4 py-3">
              <Loader className="scale-75" />
            </div>
          ) : getSearchLecturesQuery.data.length > 0 ? (
            <ul className="max-h-80 divide-y divide-text-secondary/30 overflow-auto">
              {getSearchLecturesQuery.data.map(item => (
                <li key={item.id}>
                  <Link
                    href={{
                      pathname: `/school/course/${coursePath}/lecture/${item.id}`,
                      query: {
                        [SEARCH_LECTURES_QUERY_PARAM]: searchValue,
                      },
                    }}
                    prefetch={false}
                    className="block truncate px-4 py-2.5 hover:bg-brand-primary hover:text-background-secondary"
                    onClick={() => {
                      form.setValue('search', '')
                      setIsOpen(false)
                    }}
                  >
                    {item.moduleName}: {item.lectureName}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-2.5 text-sm text-text-tertiary">
              {t('global.list.empty_list')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
